import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getShare, getShareTokenFromLocationHash } from '../../services/shareService';
import { firestore } from '../../config/firebase';
import { resolveShareRecipientDirectMeta } from '../../services/directChatService';

const btnClass =
  'text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left mt-0.5';

/**
 * @param {{
 *   conversationId: string,
 *   directConvMeta: { kind?: string, context?: Record<string, unknown> } | null | undefined,
 *   currentUser: { uid?: string } | null,
 *   currentStep?: string,
 *   currentProperty?: { id?: string } | null,
 *   onOpenDealFromChat?: (payload: { type: 'owned', propertyId: string } | { type: 'shared', shareToken: string }) => void,
 *   t: (key: string) => string,
 *   className?: string,
 * }} props
 */
export default function DealConversationDealLink({
  conversationId,
  directConvMeta,
  currentUser,
  currentStep = '',
  currentProperty = null,
  onOpenDealFromChat,
  t,
  className = '',
}) {
  const meta = useMemo(
    () => resolveShareRecipientDirectMeta(conversationId, directConvMeta, null),
    [conversationId, directConvMeta],
  );

  const shareTok = useMemo(() => {
    const c = meta?.context && typeof meta.context === 'object' ? meta.context : {};
    return String(c.shareToken || '').trim();
  }, [meta]);

  const [resolved, setResolved] = useState(null);
  const [loadState, setLoadState] = useState('idle');

  useEffect(() => {
    if (meta?.kind !== 'share_recipient' || !shareTok) {
      setResolved(null);
      setLoadState('idle');
      return undefined;
    }
    let cancelled = false;
    setLoadState('loading');
    setResolved(null);

    const finish = async () => {
      try {
        const s = await getShare(shareTok);
        if (cancelled) return;
        if (!s?.propertyId) {
          setResolved(null);
          setLoadState('error');
          return;
        }
        let ownerUid = s.uid != null && s.uid !== '' ? String(s.uid) : '';
        if (!ownerUid) {
          try {
            const ps = await getDoc(doc(firestore, 'properties', String(s.propertyId)));
            const ou = ps.data()?.uid;
            if (ou) ownerUid = String(ou);
          } catch {
            /* ignore */
          }
        }
        setResolved({
          propertyId: String(s.propertyId),
          ownerUid,
          shareToken: shareTok,
        });
        setLoadState('done');
      } catch {
        if (!cancelled) {
          setResolved(null);
          setLoadState('error');
        }
      }
    };
    finish();
    return () => {
      cancelled = true;
    };
  }, [meta?.kind, shareTok]);

  if (!onOpenDealFromChat || !currentUser?.uid) return null;
  if (meta?.kind !== 'share_recipient' || !shareTok) return null;

  if (!resolved && loadState !== 'error') {
    return <span className="text-xs text-slate-400 mt-0.5">{t('loading')}</span>;
  }

  if (!resolved && loadState === 'error') {
    return (
      <button
        type="button"
        onClick={() => onOpenDealFromChat({ type: 'shared', shareToken: shareTok })}
        className={className || btnClass}
      >
        {t('directChat.goToSharedDeal')}
      </button>
    );
  }

  if (!resolved) return null;

  const { propertyId, ownerUid, shareToken } = resolved;
  const iAmShareOwner = ownerUid && currentUser.uid === ownerUid;

  if (iAmShareOwner) {
    const onThisProperty =
      currentStep === 'dashboard' && currentProperty?.id === propertyId;
    if (onThisProperty) return null;
    return (
      <button
        type="button"
        onClick={() => onOpenDealFromChat({ type: 'owned', propertyId })}
        className={className || btnClass}
      >
        {t('directChat.goToDeal')}
      </button>
    );
  }

  const hashToken = getShareTokenFromLocationHash();
  if (hashToken === shareToken) return null;

  return (
    <button
      type="button"
      onClick={() => onOpenDealFromChat({ type: 'shared', shareToken })}
      className={className || btnClass}
    >
      {t('directChat.goToSharedDeal')}
    </button>
  );
}
